import * as api from '../api';
import { instantFor } from '../capture/dates';

// Approving a card: the ordinary routes, the same way the note writes.
// Returns the entity whose caches should refresh, the record it made or
// changed, and the page that shows it.
export async function applyProposal(proposal, { user }) {
  const p = proposal.payload || {};
  const clientId = `asst-${proposal.id || proposal.key}-${Date.now().toString(36)}`.slice(0, 64);
  const made = (entity, res, path) => ({ entity, id: res && res.data ? res.data.id : null, path });
  switch (proposal.kind) {
    case 'activity': {
      const res = await api.createActivity({
        type: p.type, description: p.summary, activity_date: instantFor(p.activity_date), account_id: p.account_id,
        contact_id: p.contact_id || null, opportunity_id: p.opportunity_id || null,
        raw_capture: proposal.raw || null, source: 'web', ai_model: proposal.ai_model || null, prompt_version: proposal.prompt_version || null, client_id: clientId,
      });
      return made('activities', res, p.account_id ? `/accounts/${p.account_id}` : '/activities');
    }
    case 'task': {
      const res = await api.createTask({
        title: p.title, due_date: p.due_date ? `${p.due_date}T09:00` : null, account_id: p.account_id || null,
        contact_id: p.contact_id || null, opportunity_id: p.opportunity_id || null, assigned_to: p.assigned_to || (user ? user.id : undefined), client_id: clientId,
      });
      return made('tasks', res, '/action-items');
    }
    case 'contact': {
      const res = await api.createContact({ name: p.name, job_title: p.title || undefined, email: p.email || undefined, phone: p.phone || undefined, influence_role: p.influence_role || undefined, account_id: p.account_id, client_id: clientId });
      return made('contacts', res, res && res.data && res.data.id ? `/contacts/${res.data.id}` : null);
    }
    case 'account': {
      const res = await api.createAccount({ name: p.name, industry: p.industry || undefined, website: p.website || undefined, notes: p.notes || undefined });
      if (p.goal || p.is_key_account) await api.updateAccountGoal(res.data.id, { goal: p.goal || undefined, is_key_account: !!p.is_key_account });
      return made('accounts', res, `/accounts/${res.data.id}`);
    }
    case 'opportunity': {
      const res = await api.createOpportunity({
        account_id: p.account_id, contact_id: p.contact_id || undefined, stage_id: p.stage_id || undefined,
        expected_close_date: p.expected_close_date || undefined, notes: p.notes || undefined,
      });
      if (p.name) await api.patchOpportunity(res.data.id, { name: p.name });
      return made('opportunities', res, `/opportunities/${res.data.id}`);
    }
    case 'field': {
      const entity = await applyField(p.apply, p.id, p.body);
      return { entity, id: p.id, path: p.path || null };
    }
    default:
      throw new Error('Nothing to apply.');
  }
}

async function applyField(how, id, body) {
  if (how === 'account_field') { await api.updateAccountField(id, body); return 'accounts'; }
  if (how === 'account_goal') { await api.updateAccountGoal(id, body); return 'accounts'; }
  if (how === 'opportunity_patch') { await api.patchOpportunity(id, body); return 'opportunities'; }
  if (how === 'task_patch') { await api.patchTask(id, body); return 'tasks'; }
  if (how === 'contact_put') { await api.updateContact(id, body); return 'contacts'; }
  throw new Error('This change cannot be applied from here.');
}

/** Whether an applied card can be taken back: a record it made, or a field with its old value. */
export function canUndo(proposal) {
  if (!proposal || proposal.state !== 'applied' || !proposal.applied || !proposal.applied.id) return false;
  if (proposal.kind === 'field') return !!(proposal.payload && proposal.payload.undo_body);
  return ['activity', 'task', 'contact', 'account', 'opportunity'].includes(proposal.kind);
}

/**
 * Take an applied card back: delete what it made, or put the field back
 * as it was. Returns the entity whose caches should refresh.
 */
export async function undoProposal(proposal) {
  if (!canUndo(proposal)) throw new Error('This one cannot be undone from here.');
  const id = proposal.applied.id;
  switch (proposal.kind) {
    case 'activity': await api.deleteActivity(id); return 'activities';
    case 'task': await api.deleteTask(id); return 'tasks';
    case 'contact': await api.deleteContact(id); return 'contacts';
    // Never takes contacts with it: if someone was added since, the account stays.
    case 'account': await api.deleteAccount(id, false); return 'accounts';
    case 'opportunity': await api.deleteOpportunity(id); return 'opportunities';
    case 'field': return applyField(proposal.payload.apply, id, proposal.payload.undo_body);
    default: throw new Error('This one cannot be undone from here.');
  }
}
